import { useEffect, useState } from "react";

import { InvestmentStyleDiagnosis } from "@/api/generated";
import { fetchInvestmentStyleDiagnosis, isRenderableDiagnosisBody } from "@/api/investmentStyleDiagnosis";
import { extractHttpStatus } from "@/utilities/extractHttpStatus";

// ホームの投資スタイル診断ミニ表示の状態。
// - loading:      判定前。バナーを出すと未診断→診断済みのちらつきになるため何も描画しない
// - diagnosed:    診断結果あり。タイプとスタンスを表示する
// - undiagnosed:  未回答。診断導線バナーを表示する
// - unavailable:  401・500 等のエラー、または 200 でも形の壊れたボディ（`{}` 等）。
//                 診断済みかどうか判別できないため「まだ診断されていません」とは言わず、非表示にする。
//                 customer_type が欠けているだけの正常レコードは unavailable ではなく diagnosed に倒し、
//                 タイプ表示のフォールバックは InvestmentStyleSummarySection 側に任せる
export type HomeInvestmentStyleDiagnosisStatus = "loading" | "diagnosed" | "undiagnosed" | "unavailable";

interface UseHomeInvestmentStyleDiagnosis {
  status: HomeInvestmentStyleDiagnosisStatus;
  diagnosis: InvestmentStyleDiagnosis | null;
}

// 結果画面の useGetInvestmentStyleDiagnosis は 404 でイントロへ強制遷移するため、ホームでは流用できない。
// ホームは「どの状態でも画面に留まる」ことが前提なので、遷移もエラー表示も行わない専用フックを用意する。
//
// UserState の完了フラグ（is_investment_style_diagnosis_complete）による GET スキップは意図的に行わない。
// フラグは診断レコードの写しにすぎず、別端末での診断完了などで実データと乖離すると
// 「まだ診断されていません」を誤表示してしまうため、常に GET で診断の有無を確認する。
export const useHomeInvestmentStyleDiagnosis = (): UseHomeInvestmentStyleDiagnosis => {
  const [diagnosis, setDiagnosis] = useState<InvestmentStyleDiagnosis | null>(null);
  const [status, setStatus] = useState<HomeInvestmentStyleDiagnosisStatus>("loading");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await fetchInvestmentStyleDiagnosis();
        if (!mounted) return;
        if (!isRenderableDiagnosisBody(data)) {
          setStatus("unavailable");
          return;
        }
        setDiagnosis(data);
        setStatus("diagnosed");
      } catch (error) {
        if (!mounted) return;
        setStatus(extractHttpStatus(error) === 404 ? "undiagnosed" : "unavailable");
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return { status, diagnosis };
};
